import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ChevronDown } from "lucide-react";

const DashboardFooter: React.FC = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accessId = searchParams.get("accessId");

  // Get the current relative URL
  const currentPath = location.pathname;

  // Determine if we're on the integration guide page
  const isIntegrationGuide = currentPath.includes("/integration-guide");

  // Create the integration guide URL
  const integrationGuideUrl = `/contribute/dashboard/integration-guide${
    accessId ? `?accessId=${accessId}` : ""
  }`;

  // Create the dashboard URL (for the back link on the integration guide page)
  const dashboardUrl = `/contribute/dashboard${
    accessId ? `?accessId=${accessId}` : ""
  }`;

  // Split the integration guide text into two lines for mobile
  const integrationGuideText = t("dashboard.integrationGuide");
  let firstLine = integrationGuideText;
  let secondLine = "";
  if (integrationGuideText.length > 18) {
    // Try to split at the first space after the midpoint
    const midpoint = Math.floor(integrationGuideText.length / 2);
    const splitIndex = integrationGuideText.indexOf(" ", midpoint);
    if (splitIndex !== -1) {
      firstLine = integrationGuideText.slice(0, splitIndex);
      secondLine = integrationGuideText.slice(splitIndex + 1);
    }
  }

  return (
    <footer className="bg-white border-t border-gray-200 py-6 mt-12">
      <div className="container mx-auto px-6">
        <div className="flex flex-col items-center space-y-4 md:flex-row md:justify-between md:space-y-0">
          {/* Links: stacked vertically on both mobile and desktop */}
          <div className="flex flex-col items-center w-full md:items-start md:w-auto">
            {isIntegrationGuide ? (
              <Link
                to={dashboardUrl}
                className="text-sm text-primary hover:underline mb-2 md:mb-0"
              >
                {t("dashboard.backToDashboard")}
              </Link>
            ) : (
              <Link
                to="/"
                className="text-sm text-primary hover:underline mb-2 md:mb-0"
              >
                {t("dashboard.backToRegistry")}
              </Link>
            )}
            {!isIntegrationGuide && (
              <Link
                to={integrationGuideUrl}
                className="text-sm text-primary hover:underline text-center md:text-left md:mt-1"
              >
                <span className="block md:hidden">
                  {firstLine}
                  <br />
                  {secondLine}
                </span>
                <span className="hidden md:inline">{integrationGuideText}</span>
              </Link>
            )}
          </div>

          {/* Language switcher */}
          <div className="flex items-center justify-center mt-2 w-full md:w-auto">
            <span className="text-sm text-gray-600 mr-1">
              {t("common.language").toUpperCase()}
            </span>
            <LanguageSwitcher />
            <ChevronDown className="h-4 w-4 text-gray-600" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default DashboardFooter;
